import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent } from 'react'
import './index.css'
import { Customizer } from './components/Customizer'
import { DPad } from './components/DPad'
import { GameCanvas } from './components/GameCanvas'
import { Leaderboard } from './components/Leaderboard'
import { playGameSound } from './game/audio'
import { createGame, getSpeed, queueDirection, startGame, stepGame, togglePause } from './game/engine'
import { BUILT_IN_PRESETS, CLASSIC_SETTINGS, decodePreset, encodePreset, isOfficialRules, makeCustom, sanitizeSettings } from './game/settings'
import { loadBest, loadLocale, loadPlayer, loadPresets, loadSettings, saveBest, saveLocale, savePlayer, savePresets, saveSettings } from './game/storage'
import type { Direction, GameSettings, Locale, Preset, ScoreCategory, ScoreEntry } from './game/types'
import { detectLocale, dictionaries, type TranslationKey } from './i18n'

function initialSettings() {
  const encoded = new URLSearchParams(window.location.search).get('preset')
  return encoded ? decodePreset(encoded) ?? CLASSIC_SETTINGS : loadSettings(CLASSIC_SETTINGS)
}

function initialLocale(): Locale {
  const requested = new URLSearchParams(window.location.search).get('lang')
  if (requested === 'ca' || requested === 'es' || requested === 'en') return requested
  return loadLocale() ?? detectLocale()
}

export default function App() {
  const [settings, setSettings] = useState<GameSettings>(initialSettings)
  const [draft, setDraft] = useState<GameSettings>(settings)
  const [game, setGame] = useState(() => createGame(settings))
  const [locale, setLocale] = useState<Locale>(initialLocale)
  const [customizerOpen, setCustomizerOpen] = useState(false)
  const [customPresets, setCustomPresets] = useState<Preset[]>(loadPresets)
  const [category, setCategory] = useState<ScoreCategory>('official')
  const [scores, setScores] = useState<ScoreEntry[]>([])
  const [scoresLoading, setScoresLoading] = useState(true)
  const [scoresError, setScoresError] = useState(false)
  const [playerName, setPlayerName] = useState(loadPlayer)
  const [scoreSaved, setScoreSaved] = useState(false)
  const [savingScore, setSavingScore] = useState(false)
  const [saveError, setSaveError] = useState(false)
  const [resultDismissed, setResultDismissed] = useState(false)
  const [copied, setCopied] = useState(false)
  const [online, setOnline] = useState(navigator.onLine)
  const [best, setBest] = useState(loadBest)
  const previousEventSeq = useRef(game.eventSeq)
  const previousStatus = useRef(game.status)
  const t = useCallback((key: TranslationKey) => dictionaries[locale][key], [locale])
  const official = isOfficialRules(settings.rules)
  const finished = game.status === 'gameover' || game.status === 'won'
  const tickDelay = 1000 / getSpeed(game, settings)

  useEffect(() => {
    document.documentElement.lang = locale
    saveLocale(locale)
  }, [locale])

  useEffect(() => saveSettings(settings), [settings])

  useEffect(() => {
    const update = () => setOnline(navigator.onLine)
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    return () => { window.removeEventListener('online', update); window.removeEventListener('offline', update) }
  }, [])

  useEffect(() => {
    if (game.status !== 'playing') return
    const interval = window.setInterval(() => setGame((current) => stepGame(current, settings)), tickDelay)
    return () => window.clearInterval(interval)
  }, [game.status, settings, tickDelay])

  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) setGame((current) => current.status === 'playing' ? togglePause(current) : current)
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  const changeDirection = useCallback((direction: Direction) => {
    setResultDismissed(true)
    setGame((current) => queueDirection(current, direction))
    if (settings.controls.haptics && 'vibrate' in navigator) navigator.vibrate(8)
  }, [settings.controls.haptics])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target?.matches('input, textarea, select, button')) return
      const key = event.key.toLowerCase()
      const mapped: [Direction, string[]][] = [
        ['up', [settings.controls.keys.up.toLowerCase(), 'arrowup', 'w']],
        ['down', [settings.controls.keys.down.toLowerCase(), 'arrowdown', 's']],
        ['left', [settings.controls.keys.left.toLowerCase(), 'arrowleft', 'a']],
        ['right', [settings.controls.keys.right.toLowerCase(), 'arrowright', 'd']],
      ]
      const direction = mapped.find(([, keys]) => keys.includes(key))?.[0]
      if (direction) { event.preventDefault(); changeDirection(direction); return }
      if (key === settings.controls.keys.pause.toLowerCase() || key === 'p' || key === ' ') {
        event.preventDefault(); setGame((current) => togglePause(current))
      }
      if (key === settings.controls.keys.restart.toLowerCase() || key === 'r') {
        event.preventDefault(); restart()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  })

  useEffect(() => {
    if (previousEventSeq.current === game.eventSeq) return
    previousEventSeq.current = game.eventSeq
    playGameSound(game.event, settings.audio.volume, settings.audio.muted)
    if (game.event === 'eat' && settings.controls.haptics && 'vibrate' in navigator) navigator.vibrate([10, 18, 10])
  }, [game.event, game.eventSeq, settings.audio, settings.controls.haptics])

  useEffect(() => {
    const wasFinished = previousStatus.current === 'gameover' || previousStatus.current === 'won'
    previousStatus.current = game.status
    if (finished && !wasFinished) {
      saveBest(game.score)
      setBest((current) => Math.max(current, game.score))
      setScoreSaved(false)
      setSaveError(false)
      setResultDismissed(false)
    }
  }, [finished, game.score, game.status])

  useEffect(() => {
    let active = true
    let unsubscribe: () => void = () => undefined
    void import('./services/scores').then(({ subscribeScores }) => {
      if (!active) return
      unsubscribe = subscribeScores(category, (next) => {
        if (!active) return
        setScores(next)
        setScoresLoading(false)
      }, () => {
        if (!active) return
        setScoresError(true)
        setScoresLoading(false)
      })
    }).catch(() => { if (active) { setScoresError(true); setScoresLoading(false) } })
    return () => { active = false; unsubscribe() }
  }, [category])

  function restart(nextSettings = settings) {
    setGame(createGame(nextSettings))
    setResultDismissed(false)
    setScoreSaved(false)
    setSaveError(false)
  }

  function toggleGame() {
    if (game.status === 'ready') setGame((current) => startGame(current))
    else if (game.status === 'playing' || game.status === 'paused') setGame((current) => togglePause(current))
    else restart()
  }

  function changeCategory(next: ScoreCategory) {
    setCategory(next)
    setScores([])
    setScoresLoading(true)
    setScoresError(false)
  }

  function changePlayerName(value: string) {
    const next = value.slice(0, 24)
    setPlayerName(next)
    savePlayer(next)
  }

  function openCustomizer() {
    setDraft(settings)
    setCustomizerOpen(true)
    if (game.status === 'playing') setGame((current) => togglePause(current))
  }

  function applyRules() {
    const next = makeCustom(sanitizeSettings(draft))
    setSettings(next)
    setDraft(next)
    restart(next)
  }

  function applyPreset(preset: Preset) {
    const next = sanitizeSettings(preset.settings)
    setSettings(next)
    setDraft(next)
    restart(next)
  }

  function applyLive(nextValue: GameSettings) {
    const next = sanitizeSettings(nextValue)
    setSettings(next)
    setDraft((current) => ({ ...current, appearance: next.appearance, controls: next.controls, audio: next.audio, accessibility: next.accessibility }))
  }

  function saveCustomPreset(name: string) {
    const id = `custom-${Date.now()}`
    const nextSettings = makeCustom(sanitizeSettings({ ...settings, rules: draft.rules, presetId: id }))
    const next = [...customPresets, { id, name: name.trim().slice(0, 24), settings: nextSettings, createdAt: Date.now() }].slice(-20)
    setCustomPresets(next)
    savePresets(next)
  }

  async function sharePreset() {
    const url = new URL(window.location.href)
    url.searchParams.set('preset', encodePreset(settings))
    url.searchParams.set('lang', locale)
    await navigator.clipboard?.writeText(url.toString())
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1600)
  }

  async function handleSaveScore(event: FormEvent) {
    event.preventDefault()
    if (scoreSaved || savingScore || !finished) return
    setSavingScore(true)
    setSaveError(false)
    try {
      const service = await import('./services/scores')
      if (!service.isValidPlayerName(playerName)) throw new Error('invalid-name')
      const normalized = service.normalizePlayerName(playerName)
      const configHash = await service.settingsHash(settings)
      const scoreCategory: ScoreCategory = official ? 'official' : 'custom'
      const entry = service.buildScoreEntry(normalized, game.score, game.foodEaten, game.snake.length, game.elapsedMs, scoreCategory, settings, configHash)
      await service.saveScore(entry)
      setPlayerName(normalized)
      savePlayer(normalized)
      setScoreSaved(true)
      if (category === scoreCategory) setScores((current) => service.sortScores([...current, { ...entry, createdAt: Date.now() }]).slice(0, 50))
    } catch {
      setSaveError(true)
    } finally {
      setSavingScore(false)
    }
  }

  const cssVariables = useMemo(() => ({
    '--accent': settings.appearance.snake,
    '--food': settings.appearance.food,
    '--board-bg': settings.appearance.background,
  } as CSSProperties), [settings.appearance])
  const status = t(game.status)
  const showDpad = settings.controls.dpad !== 'off'

  return <div className={`app ${settings.accessibility.highContrast ? 'high-contrast' : ''} ${settings.accessibility.reducedMotion ? 'reduce-motion' : ''}`} style={cssVariables}>
    <header className="topbar">
      <div className="brand"><span className="brand-mark">S</span><div><h1>SNAKE</h1><p>by olerida</p></div></div>
      <p className="top-tagline">{t('tagline')} <span>/</span> 2026</p>
      <div className="top-actions">
        <div className="locale-switch" aria-label="Language">
          {(['ca', 'es', 'en'] as Locale[]).map((item) => <button type="button" key={item} aria-pressed={locale === item} onClick={() => setLocale(item)}>{item.toUpperCase()}</button>)}
        </div>
        <button type="button" className="customize-button" onClick={openCustomizer}>{t('customize')} <span>↗</span></button>
      </div>
    </header>

    <main className="workspace">
      <section className="play-column" aria-label={t('play')}>
        <div className="stage-head">
          <div><span className="eyebrow">{official ? t('officialLabel') : t('customLabel')}</span><strong className="status-label"><i className={game.status} />{status}</strong></div>
          <div className="hud">
            <div><span>{t('score')}</span><strong>{game.score.toLocaleString()}</strong></div>
            <div><span>{t('best')}</span><strong>{best.toLocaleString()}</strong></div>
            <div><span>{t('length')}</span><strong>{game.snake.length}</strong></div>
            <div><span>{t('level')}</span><strong>{game.level}</strong></div>
          </div>
        </div>

        <div className="board-frame">
          <GameCanvas state={game} settings={settings} label={`${status}. ${t('score')}: ${game.score}`} onDirection={changeDirection} />
          <div className={`board-overlay ${game.status}`}>
            {game.status !== 'playing' && <div className="overlay-copy">
              <span>{official ? 'CLASSIC 24×24' : `${settings.rules.columns}×${settings.rules.rows} / ${settings.rules.startSpeed}×`}</span>
              <strong>{status}</strong>
              <button type="button" onClick={toggleGame}>{game.status === 'paused' ? t('resume') : game.status === 'ready' ? t('start') : t('restart')}</button>
            </div>}
          </div>
          <div className="corner corner-tl" /><div className="corner corner-tr" /><div className="corner corner-bl" /><div className="corner corner-br" />
        </div>

        <div className="game-controls">
          <div className="primary-controls"><button type="button" className="play-button" onClick={toggleGame}><span>{game.status === 'playing' ? 'Ⅱ' : '▶'}</span>{game.status === 'playing' ? t('paused') : game.status === 'paused' ? t('resume') : finished ? t('restart') : t('start')}</button><button type="button" className="restart-button" onClick={() => restart()}>↻ {t('restart')}</button></div>
          <div className="control-help"><span>{t('howTo')}</span><small>{online ? t('online') : t('offline')} <i className={online ? 'online' : ''} /></small></div>
        </div>
        {showDpad && <div className={`dpad-wrap dpad-${settings.controls.dpad}`}><DPad onDirection={changeDirection} /><p>{t('swipeHint')}</p></div>}
      </section>

      <aside className="side-column">
        <div className="run-spec">
          <span className="eyebrow">RUN SPEC</span>
          <dl><div><dt>{t('board')}</dt><dd>{settings.rules.columns}×{settings.rules.rows}</dd></div><div><dt>{t('speed')}</dt><dd>{getSpeed(game, settings).toFixed(1)}×</dd></div><div><dt>{t('walls')}</dt><dd>{t(settings.rules.wallMode)}</dd></div><div><dt>{t('obstacles')}</dt><dd>{settings.rules.obstaclePercent}%</dd></div></dl>
        </div>
        <div className="player-profile">
          <label htmlFor="player-name"><span className="eyebrow">PLAYER</span>{t('playerName')}</label>
          <input id="player-name" value={playerName} onChange={(event) => changePlayerName(event.target.value)} minLength={2} maxLength={24} placeholder="olerida" autoComplete="nickname" />
          <small>{t('publicNotice')}</small>
        </div>
        <Leaderboard category={category} onCategory={changeCategory} scores={scores} loading={scoresLoading} error={scoresError} t={t} />
      </aside>
    </main>

    <footer><span>BUILT FOR THE TURN</span><strong>SNAKE / by olerida</strong><span>CA · ES · EN</span></footer>

    <Customizer open={customizerOpen} settings={settings} draft={draft} builtIns={BUILT_IN_PRESETS} customPresets={customPresets} copied={copied} t={t} onClose={() => setCustomizerOpen(false)} onDraft={setDraft} onLive={applyLive} onApply={applyRules} onPreset={applyPreset} onSavePreset={saveCustomPreset} onShare={() => void sharePreset()} onReset={() => applyPreset(BUILT_IN_PRESETS[0])} />

    {finished && !resultDismissed && <div className="result-backdrop" role="presentation">
      <form className="result-dialog" role="dialog" aria-modal="true" aria-labelledby="result-title" onSubmit={handleSaveScore}>
        <span className="eyebrow">{official ? t('officialLabel') : t('customLabel')}</span>
        <h2 id="result-title">{t('result')}</h2>
        <strong className="result-score">{game.score.toLocaleString()}</strong>
        <span className="result-unit">{t('score').toUpperCase()} / {game.foodEaten} FOOD</span>
        <label><span>{t('playerName')}</span><input value={playerName} onChange={(event) => changePlayerName(event.target.value)} minLength={2} maxLength={24} placeholder="olerida" autoComplete="nickname" /></label>
        <p>{t('publicNotice')}</p>
        {saveError && <p className="form-error" role="alert">{t('saveError')}</p>}
        <button type="submit" disabled={scoreSaved || savingScore || playerName.trim().length < 2}>{scoreSaved ? t('saved') : savingScore ? t('saving') : t('saveScore')}</button>
        <button type="button" className="dialog-secondary" onClick={() => { setResultDismissed(true); restart() }}>{t('restart')}</button>
      </form>
    </div>}
  </div>
}
